/**
 * Sign-in form. For Fase 0 only Google sign-in is supported.
 *
 * Accessibility: semantic HTML, keyboard-focusable button, aria-live error
 * region, min 44px touch target. No native dialogs (CLAUDE.md § 9).
 */

import { useState } from "react";
import { useAuth } from "./AuthContext.js";

export function SignInForm(): JSX.Element {
  const { signInGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInGoogle();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Error al iniciar sesión.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section aria-label="sign-in" className="space-y-4">
      <h1 className="text-2xl font-bold text-zinc-100">AutoDieta IA</h1>
      <p className="text-sm text-zinc-400">Iniciá sesión para continuar.</p>

      <button
        type="button"
        aria-label="Continuar con Google"
        disabled={loading}
        onClick={() => void handleGoogleSignIn()}
        className="flex min-h-[44px] w-full items-center justify-center gap-3 rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-sm font-medium text-zinc-100 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50"
      >
        {loading ? (
          <>
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-600 border-t-blue-400"
            />
            Iniciando sesión…
          </>
        ) : (
          <>
            {/* Google "G" icon (inline SVG, optimized) */}
            <svg
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                fill="#4285F4"
              />
              <path
                d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
                fill="#34A853"
              />
              <path
                d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                fill="#FBBC05"
              />
              <path
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                fill="#EA4335"
              />
            </svg>
            Continuar con Google
          </>
        )}
      </button>

      {error ? (
        <p
          role="alert"
          aria-live="assertive"
          className="rounded-lg border border-red-900/50 bg-red-950/20 px-4 py-2 text-sm text-red-400"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}
